import { useEffect } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { gtag } from "@/utils/gtag";
import { SEO } from "@/components/SEO";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ReservationForm } from "@/components/ReservationForm";
import { OptimizedImage } from "@/components/OptimizedImage";
import { useSumos } from "@/hooks/useSumos";
import { useSumoImages } from "@/hooks/useSumoImages";
import { Users, Shield, Clock, CheckCircle, Star } from "lucide-react";
import sumoWrestling from "@/assets/sumo-wrestling.jpg";

const Sumos = () => {
  const { sumos, loading } = useSumos();
  const sumo = sumos[0]; // Get first (and likely only) sumo
  const { images, isLoading: imagesLoading } = useSumoImages(sumo?.id || '');

  // Track page view and product for Google Ads
  useEffect(() => {
    // Track page view
    gtag('event', 'page_view', {
      page_title: 'Sumos Gonflables - Château de Clémentine',
      page_location: window.location.href,
      send_to: 'AW-1049756763'
    });

    // Track view_item when sumo data is loaded
    if (sumo) {
      gtag('event', 'view_item', {
        currency: 'EUR',
        value: sumo.price || 50,
        items: [{
          item_id: sumo.id,
          item_name: sumo.title,
          item_category: 'Sumo Gonflable',
          price: sumo.price || 50,
          quantity: 1
        }]
      });
    }
  }, [sumo]);

  if (loading) {
    return <div>Chargement...</div>;
  }

  const sumoFeatures = [
    { icon: Users, title: "Pour adultes", description: "Minimum 150cm requis" },
    { icon: Shield, title: "Sécurisé", description: "Tapis de sécurité inclus" },
    { icon: Clock, title: "Installation rapide", description: "Prêt en 10 minutes" },
    { icon: CheckCircle, title: "Tout inclus", description: "Costumes et protection" }
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="Location Sumos Gonflables - Château de Clémentine"
        description="Louez nos costumes sumos gonflables pour vos événements. Combat amusant et sécurisé à partir de 50€. Livraison en Belgique."
        keywords="sumo gonflable, location sumo, combat sumo, animation événement, fête adulte, Rhode-Saint-Genèse, Belgique"
        canonicalUrl="https://chateaudeclementine.com/sumos"
      />
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-sky">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-6 gradient-primary text-white border-0">
                Animation Adultes
              </Badge>
              <h1 className="text-4xl md:text-6xl font-display font-bold mb-6">
                <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                  {sumo?.title || 'Sumos Gonflables'}
                </span>
              </h1>
              <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                {sumo?.description || 'Vivez des moments hilarants avec nos costumes sumos gonflables !'}
              </p>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
              {sumoFeatures.map((feature, index) => (
                <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                  <CardContent className="pt-8">
                    <feature.icon className="w-12 h-12 text-primary mx-auto mb-4" />
                    <h3 className="font-semibold mb-2">{feature.title}</h3>
                    <p className="text-muted-foreground text-sm">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Main Product Section */}
        <section className="py-16 bg-gradient-sky">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Image */}
              <div className="relative">
                <Card className="overflow-hidden shadow-card">
                  <div className="relative h-96">
                    <img 
                      src={images.length > 0 ? images[0].image_url : sumoWrestling} 
                      alt="Combat sumo gonflable"
                      className="w-full h-full object-cover"
                      loading="eager"
                    />
                    <div className="absolute top-4 right-4">
                      <Badge variant="secondary" className="bg-white/90 text-primary font-bold text-lg px-4 py-2">
                        {sumo?.price || '50€'}
                      </Badge>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Details */}
              <div>
                <h2 className="text-3xl font-display font-bold mb-6">
                  {sumo?.title || 'Combat Sumo Gonflable'}
                </h2>
                <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                  {sumo?.description || 'Transformez votre événement en arène de combat hilarant !'}
                </p>

                {/* Included Items */}
                {sumo?.features && (
                  <div className="mb-8">
                    <h3 className="font-semibold mb-4 flex items-center text-lg">
                      <Star className="w-5 h-5 mr-2 text-accent" />
                      Inclus dans la location
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {sumo.features.map((item, index) => (
                        <div key={index} className="flex items-center">
                          <CheckCircle className="w-4 h-4 text-primary mr-2 flex-shrink-0" />
                          <span className="text-sm">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Specifications */}
                {sumo?.specifications && (
                  <div className="grid grid-cols-2 gap-4 mb-8">
                    {Object.entries(sumo.specifications as Record<string, string>).map(([key, value]) => (
                      <div key={key} className="bg-white/50 p-4 rounded-lg">
                        <h4 className="font-semibold text-sm text-muted-foreground capitalize">
                          {key.replace('_', ' ')}
                        </h4>
                        <p className="font-bold">{String(value)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Photos Gallery Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <Badge className="mb-4 gradient-primary text-white border-0">Galerie</Badge>
              <h2 className="text-3xl font-display font-bold mb-4">Nos Sumos en Action</h2>
              <p className="text-muted-foreground">
                Découvrez nos sumos gonflables en images
              </p>
            </div>

            {/* Photos Grid */}
            {imagesLoading ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">Chargement des photos...</p>
              </div>
            ) : images.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {images.map((image, index) => (
                  <Card key={image.id} className="overflow-hidden shadow-card hover:shadow-playful transition-all duration-300">
                    <div className="aspect-[4/3] overflow-hidden">
                      <img
                        src={image.image_url}
                        alt={image.alt_text || `Photo sumo ${index + 1}`}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        loading="eager"
                      />
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-12 bg-gradient-sky">
                <div className="text-center">
                  <p className="text-muted-foreground">
                    Photos bientôt disponibles !
                  </p>
                </div>
              </Card>
            )}
          </div>
        </section>

        {/* Contact Section with Form */}
        <section className="py-20 bg-white" id="contact">
          <div className="container mx-auto px-4">
            {/* Section Header */}
            <div className="text-center mb-16">
              <Badge className="mb-4 gradient-primary text-white border-0">Réservation</Badge>
              <h2 className="text-4xl md:text-5xl font-display font-bold mb-6">
                <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                  Réservez vos Sumos
                </span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                Réservez dès maintenant nos sumos gonflables et garantissez des éclats de rire 
                à votre prochain événement !
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <ReservationForm preSelectedChateau="sumos" />
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Sumos;